<script setup lang="ts">
const DEFAULT_MAX_PAGES = 9
const MIN_PAGES = 3

interface Props {
  maxPage: number
  showPages?: number
  load: Function
  search?: boolean
}
const { maxPage, showPages = DEFAULT_MAX_PAGES, load } = defineProps<Props>()

const { query, path } = useRoute()

const page = ref(Number(query.p) || 1)
const limit = computed(() => showPages < MIN_PAGES ? MIN_PAGES - 2 : showPages - 2)
const length = computed(() => maxPage - 2 < limit.value ? maxPage - 2 : limit.value)
const start = computed(() => {
  if (page.value - Math.floor(length.value / 2) < 2) {
    return 2
  } else if (page.value + Math.floor(length.value / 2) >= maxPage) {
    return maxPage - length.value
  }
  return page.value - Math.floor(length.value / 2)
})
const showFirstEllipsis = computed(() => start.value - 1 !== 1)
const showLastEllipsis = computed(() => start.value + length.value < maxPage)
const visiblePages = computed(() => Array.from({ length: length.value }).map((_, i) => i + start.value))

const getUrl = (index: number) => index === 1 ? path : `?p=${index}`
const isActive = (index: number) => index === page.value
const loadPage = async ({ type, p, path, search }: { type: string, p: number, path: string, search?: boolean }) => {
  await load({ type, p, path, search })
  page.value = p
  window.scrollTo(0, 0)
}
</script>

<template>
  <ul class="flex flex-wrap justify-center gap-[10px] my-[10px]">
    <li v-if="maxPage > 1" class="inline-block">
      <a
        :href="getUrl(page - 1)"
        class="flex justify-center items-center w-[30px] h-[30px] border border-solid border-gray-200"
        :class="{ 'pointer-events-none cursor-default text-secondary-disabled': page === 1 }"
        @click.prevent="loadPage({ type: 'p', p: page - 1, path, search })"
      >
        <slot name="prev">
          <i class="icon-arrow-button -rotate-180" />
        </slot>
      </a>
    </li>
    <li class="inline-block">
      <a
        :href="getUrl(1)"
        class="flex justify-center items-center w-[30px] h-[30px] border border-solid border-gray-200"
        :class="{'bg-secondary text-white': isActive(1)}"
        @click.prevent="loadPage({ type: 'p', p: 1, path, search })"
      >
        1
      </a>
    </li>
    <template v-if="maxPage > 1">
      <li v-if="showFirstEllipsis" class="inline-block">
        <div class="flex justify-center items-center w-[30px] h-[30px] border border-solid border-gray-200">
          <slot name="ellipsis">
            ...
          </slot>
        </div>
      </li>
      <li v-for="index in visiblePages" :key="index" class="inline-block">
        <a
          :href="getUrl(index)"
          class="flex justify-center items-center w-[30px] h-[30px] border border-solid border-gray-200"
          :class="{ 'bg-secondary text-white': isActive(index) }"
          @click.prevent="loadPage({ type: 'p', p: index, path, search })"
        >
          {{ index }}
        </a>
      </li>
      <li v-if="showLastEllipsis" class="inline-block">
        <div class="flex justify-center items-center w-[30px] h-[30px] border border-solid border-gray-200">
          <slot name="ellipsis">
            ...
          </slot>
        </div>
      </li>
      <li class="inline-block">
        <a
          :href="getUrl(maxPage)"
          class="flex justify-center items-center w-[30px] h-[30px] border border-solid border-gray-200"
          :class="{ 'bg-secondary text-white': isActive(maxPage) }"
          @click.prevent="loadPage({ type: 'p', p: maxPage, path, search })"
        >
          {{ maxPage }}
        </a>
      </li>
      <li class="inline-block">
        <a
          :href="getUrl(page + 1)"
          class="flex justify-center items-center w-[30px] h-[30px] border border-solid border-gray-200"
          :class="{ 'pointer-events-none cursor-default text-secondary-disabled': page === maxPage }"
          @click.prevent="loadPage({ type: 'p', p: page + 1, path, search })"
        >
          <slot name="next">
            <i class="icon-arrow-button" />
          </slot>
        </a>
      </li>
    </template>
  </ul>
</template>
